/**
 * The one rule for an href that came from authored content.
 * ---------------------------------------------------------------------------
 * Tips and announcements are written by people with a content role and read by
 * every employee, so a link in them is untrusted input rendered on the most
 * visited page in the app. React does not block `javascript:` hrefs (it only
 * warns), so the check has to be ours, and it has to be in one place: the
 * renderer and the editor's validation hint must agree on what is allowed.
 *
 * Allowed:
 *   https://example.com/x, http://...   absolute web links
 *   /?view=LeaveRequests                 in-app links, one leading slash
 *
 * Refused (returns null):
 *   javascript:, data:, vbscript:, mailto: and every other scheme
 *   //evil.example   protocol-relative: leaves the site while looking relative
 *   /\evil.example   browsers read the backslash as a slash, so the same thing
 *   anything containing whitespace or control characters
 *   relative paths without a leading slash (they resolve against whatever page
 *   the reader happens to be on)
 */

// eslint-disable-next-line no-control-regex
const WHITESPACE_OR_CONTROL = /[\s\u0000-\u001F\u007F]/;

export function safeContentHref(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const url = raw.trim();
  if (!url || WHITESPACE_OR_CONTROL.test(url)) return null;

  if (url.startsWith('/')) {
    // A second character of / or \ makes it protocol-relative.
    return url[1] === '/' || url[1] === '\\' ? null : url;
  }

  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
      return parsed.hostname ? url : null;
    } catch {
      return null;
    }
  }

  return null;
}

/** True for an absolute http(s) link, which the renderer opens in a new tab. */
export function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

/** The hint an editor shows beside a link field, worded from the same rule. */
export const SAFE_HREF_HINT =
  'Use a full web address starting with https:// or an in-app link starting with a single / (for example /?view=LeaveRequests).';
